// src/services/userProfileService.ts

import type { User } from '../types/auth'

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? ''

/**
 * Récupère les informations complètes du profil utilisateur.
 * Endpoint : GET /api/user/profile
 */
export async function fetchUserProfile(
  token: string
): Promise<User & { id?: string; createdAt?: string; lastLogin?: string }> {
  const url = `${API_BASE}/api/user/profile`
  console.debug('[userProfileService] fetchUserProfile → GET', url)

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  console.debug('[userProfileService] fetchUserProfile ← status', response.status)

  if (!response.ok) {
    let message = 'Impossible de charger le profil.'
    try {
      const data = await response.json()
      if (data?.message || data?.error) message = data.message || data.error
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  const raw = await response.json()
  const normalized = {
    ...raw,
    username: raw.username || raw.UserName || raw.userName || '',
    email: raw.email || raw.Email || '',
  }
  return normalized
}

/**
 * Met à jour le profil utilisateur via JSON Patch.
 * Endpoint : PATCH /api/user/profile
 * 
 * Les mises à jour sont envoyées sous forme de JSON Patch operations.
 * Ex: [{ op: "replace", path: "/UserName", value: "newname" }]
 */
export async function updateUserProfile(
  token: string,
  updates: Record<string, any>
): Promise<User & { id?: string; createdAt?: string; lastLogin?: string }> {
  const url = `${API_BASE}/api/user/profile`

  // Convertir les updates simples en JSON Patch operations
  const patchOps = Object.entries(updates).map(([key, value]) => ({
    op: 'replace',
    // Capitaliser la première lettre pour ASP.NET (UserName au lieu de username)
    path: `/${key.charAt(0).toUpperCase() + key.slice(1)}`,
    value,
  }))

  console.debug('[userProfileService] updateUserProfile → PATCH', url, patchOps)

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json-patch+json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(patchOps),
  })

  console.debug('[userProfileService] updateUserProfile ← status', response.status)

  if (!response.ok) {
    let message = 'Impossible de mettre à jour le profil.'
    try {
      const data = await response.json()
      if (data?.error) message = data.error
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  const raw = await response.json()
  const normalized = {
    ...raw,
    username: raw.username || raw.UserName || raw.userName || '',
    email: raw.email || raw.Email || '',
  }
  return normalized
}

/**
 * Supprime le compte utilisateur.
 * Endpoint : DELETE /api/user/profile
 * Retour : NoContent (204)
 */
export async function deleteUserProfile(token: string): Promise<void> {
  const url = `${API_BASE}/api/user/profile`
  console.debug('[userProfileService] deleteUserProfile → DELETE', url)

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  console.debug('[userProfileService] deleteUserProfile ← status', response.status)

  if (!response.ok && response.status !== 204) {
    let message = 'Impossible de supprimer le compte.'
    try {
      const data = await response.json()
      if (data?.error) message = data.error
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
}
